// ── Participant access / history section (Wave 4, READ) ─────────────────────
// CONTRACT CHECK (blueprint §1, trust source): there is NO participant-scoped
// access-history route in the registry. The only history surface is the
// STAFF-ONLY event-history (history.list, GET /event-history) whose roleScope
// note says "participant has no operational history; coach/company_admin see
// human-readable scoped summaries." There is also NO documented `participantId`
// filter param on that route. So we do NOT invent a participant history route.
//
// Honest sourcing: this section reads the staff event-history and surfaces the
// entries whose human-readable summary references THIS participant (by id or
// email — the only client-side correlation available). If the staff history is
// denied for the caller's role (403 -> permission-denied) or empty, it renders
// the honest posture — never a fabricated access log. If correlation finds no
// matching entries we show an honest "no recorded activity" empty, NOT a blank.
//
// The server scopes the event-history per role; this section renders only what
// is returned. A richer participant-scoped access-history route, if it ever
// lands, would replace this client correlation (flagged below).

import { CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingHistoryEventDto, CoachingParticipantDto, ListResponse } from "../../../services/coaching-dtos";
import { ParticipantDetailSection } from "./participant-detail-section";
import {
  useParticipantAccessHistoryCollection,
  type ParticipantAccessHistoryRow,
} from "./participant-access-history-collection";

const services = getCoachingServices();


/** Keep only the staff-history entries whose summary references this participant. */
export function correlateParticipantHistory(
  events: CoachingHistoryEventDto[],
  participant: Pick<CoachingParticipantDto, "id" | "email">
): CoachingHistoryEventDto[] {
  const id = (participant.id ?? "").trim().toLowerCase();
  const email = (participant.email ?? "").trim().toLowerCase();
  return events.filter((event) => {
    const haystack = `${event.summary ?? ""} ${event.action ?? ""}`.toLowerCase();
    return (id.length > 0 && haystack.includes(id)) || (email.length > 0 && haystack.includes(email));
  });
}

export function ParticipantAccessHistorySection({
  participant
}: {
  participant: Pick<CoachingParticipantDto, "id" | "email">;
}) {
  const query = useHonestQuery<ListResponse<CoachingHistoryEventDto>>(
    coachingQueryKeys.list("history.list"),
    () => services.history.list()
  );

  const all = query.data?.items ?? [];
  const correlated = correlateParticipantHistory(all, participant) as ParticipantAccessHistoryRow[];
  const collection = useParticipantAccessHistoryCollection(correlated);

  // The staff history loaded with data but nothing referenced this participant:
  // surface an honest empty ("no recorded activity"), NOT a blank or a fabricated
  // access entry. If the read itself is denied/unavailable/empty, that posture
  // flows straight through to the section shell.
  const posture =
    query.posture === null && correlated.length === 0 && all.length > 0 ? "empty" : query.posture;

  return (
    <ParticipantDetailSection
      title="Access & history"
      subtitle="Recorded coaching activity referencing this participant, from the staff event history."
      posture={posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No recorded activity"
      emptyMessage="No staff event-history entries reference this participant yet."
    >
      <CollectionToolbar collection={collection} className="rounded-xl border border-line" stableChipNames />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Participant access and history"
          emptyTitle="No recorded activity"
          emptyMessage="No staff event-history entries reference this participant yet."
        />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </ParticipantDetailSection>
  );
}

